import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { CustomComponent } from './types';
import { ChevronDown, ChevronUp } from 'lucide-react';

export const ArtifactPre: CustomComponent = ({ node, children, ...props }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);

  const checkOverflow = useCallback(() => {
    if (preRef.current) {
      // For artifacts in the panel, show full content without artificial limits
      // Only collapse if content is extremely long (over 50 lines of typical code)
      const contentHeight = preRef.current.scrollHeight;
      const visibleHeight = 2000; // Much higher threshold - roughly 50+ lines of code

      const needsCollapseButton = contentHeight > visibleHeight;
      setHasOverflow(needsCollapseButton);

      if (needsCollapseButton && !hasOverflow) {
        setIsCollapsed(true);
      } else if (!needsCollapseButton) {
        setIsCollapsed(false);
      }
    }
  }, [hasOverflow]);

  // Check on initial render and when children change
  useEffect(() => {
    checkOverflow();
  }, [children, checkOverflow]);

  // Also check on window resize
  useEffect(() => {
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  // Much higher limits for artifacts - show most content without truncation
  const preMaxHeight = isCollapsed ? '1800px' : 'none'; // Show almost everything, unlimited when expanded
  const prePaddingBottom = isCollapsed && hasOverflow ? '40px' : '16px';

  return (
    <div className="relative my-4 w-full">
      <pre
        ref={preRef}
        {...props}
        className="custom-pre-block"
        style={{
          maxHeight: preMaxHeight,
          paddingBottom: prePaddingBottom,
        }}
      >
        {children}
      </pre>

      {hasOverflow && (
        <div
          className="absolute bottom-0 left-0 right-0 flex justify-center items-end"
          style={{ paddingBottom: '4px' }}
        >
          {/* Gradient shown only when collapsed */}
          <div
            className={`absolute bottom-0 left-0 right-0 h-20 pointer-events-none ${
              isCollapsed ? 'block' : 'hidden'
            }`}
            style={{
              background: 'linear-gradient(to top, #f8f9fc 60%, transparent)',
            }}
          />
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="relative px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded border border-slate-200 hover:bg-slate-200 z-10 shadow-md"
          >
            <span className="flex items-center gap-1">
              {isCollapsed ? (
                <>Show more <ChevronDown className="w-3 h-3" /></>
              ) : (
                <>Show less <ChevronUp className="w-3 h-3" /></>
              )}
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ArtifactPre;